<template>
  <div class="_pbs">
    <app-step-primary-subscriber></app-step-primary-subscriber>

    <div container>
      <list-error-warnings :warnings="listErrorWarnings" />
    </div>

    <form @submit.prevent="search">
      <div container>
        <div grid>
          <div column="3">
            <client-health-id
              v-model="v$.clientId.$model"
              :errorMessage="clientIdError"
              :invalid="v$.clientId.$invalid"
              valueKey="clientId"
              label="Client's Member ID:"
              :enable-mask="ssnMatchesClientHealthId(v$.ssn.$model, v$.clientId.$model)"
            />
          </div>
          <div column="3" v-if="isInternalUser">
            <app-input
              v-model="v$.fileBoundId.$model"
              :required="isInternalUser"
              :errorMessage="fileBoundIdError"
              :invalid="v$.fileBoundId.$invalid"
              valueKey="fileBoundId"
              label=" Filebound ID:"
            />
          </div>
        </div>
      </div>
      <div container>
        <div grid>
          <!-- uncomment this if they decide they want to be able to pick a parent on this page -->
          <!-- <div column v-if="isInternalUser">
            <app-select
              :required="true"
              :hasBlankOption="false"
              label="Parent Group"
              name="parentGroup"
              :options="parentGroupDropdownOptions"
              @click.stop.prevent="handleGroupClicked($event)"
              @keydown.stop.prevent="handleGroupClicked($event)"
              v-model="v$.groupCk.$model"
              :errorMessage="parentGroupCkError"
              :invalid="v$.parentGroupCk.$invalid"
            ></app-select>
          </div> -->
          <div column>
            <app-select
              :required="true"
              :hasBlankOption="false"
              label="Group"
              name="group"
              :options="groupDropdownOptions"
              @click.stop.prevent="handleGroupClicked($event)"
              @keydown.stop.prevent="handleGroupClicked($event)"
              v-model="v$.groupCk.$model"
              :errorMessage="groupCkError"
              :invalid="v$.groupCk.$invalid"
            ></app-select>
            <!-- <div v-if="selected.parentGroupName !== ''" class="filter-text">
              (filtered by: {{ selected.parentGroupName }})
            </div> -->
          </div>
          <div column>
            <app-select
              :required="true"
              :hasBlankOption="false"
              label="Subgroup"
              name="subgroup"
              :options="subGroupDropdownOptions"
              @click.stop.prevent="handleGroupClicked($event)"
              @keydown.stop.prevent="handleGroupClicked($event)"
              v-model="v$.subGroupCk.$model"
              :errorMessage="subGroupCkError"
              :invalid="v$.subGroupCk.$invalid"
            ></app-select>
          </div>
        </div>

        <div grid>
          <div column>
            <div
              v-if="requestingNewClassPlans"
              class="_text-center"
              >
              Retrieving class information
              <async-loader
                class="small _ptxs _pbxs"
              />
            </div>
            <app-select
              v-else
              v-model="v$.classId.$model"
              :required="true"
              :errorMessage="classIdError"
              :invalid="v$.classId.$invalid"
              label="Class"
              name="class"
              :options="computedFilteredClassOptions"
            />
          </div>
          <div column>
            <div
              v-if="requestingNewClassPlans"
              class="_text-center"
              >
              Retrieving plan information
              <async-loader
                class="small _ptxs _pbxs"
              />
            </div>
            <app-select
              v-else
              v-model="v$.planId.$model"
              :required="true"
              :errorMessage="planIdError"
              :invalid="v$.planId.$invalid"
              label="Plan"
              :disabled="computedFilteredPlanOptions.length === 0"
              name="plan"
              :options="computedFilteredPlanOptions"
            />
          </div>
          <div column>
            <app-date-input
              v-model="effectiveDate"
              :required="true"
              :errorMessage="listErrorFilter('effectiveDate', effectiveDateError)"
              :invalid="v$.effectiveDate.$invalid"
              name="effectiveDate"
              label="Effective Date:"
            />
          </div>
          <div column>
            <app-date-input
              v-model="v$.termDate.$model"
              :errorMessage="listErrorFilter('termDate', termDateError)"
              :invalid="v$.termDate.$invalid"
              valueKey="termDate"
              label="Term Date:"
            />
          </div>
        </div>

        <div grid>
          <div column>
            <app-input
              v-model="v$.firstName.$model"
              :required="true"
              :errorMessage="listErrorFilter('firstName', firstNameError)"
              :invalid="v$.firstName.$invalid"
              valueKey="firstName"
              label="First Name:"
            />
          </div>
          <div column>
            <app-input
              v-model='v$.middleInitial.$model'
              :invalid="v$.middleInitial.$invalid"
              :errorMessage="middleInitialError"
              valueKey='middleInitial'
              label='Middle Initial:'/>
          </div>
          <div column>
            <app-input
              v-model="v$.lastName.$model"
              :required="true"
              :errorMessage="listErrorFilter('lastName', lastNameError)"
              :invalid="v$.lastName.$invalid"
              valueKey="lastName"
              label="Last Name:"
            />
          </div>
          <div column>
            <app-input
              v-model='v$.suffix.$model'
              :invalid="v$.suffix.$invalid"
              :errorMessage="suffixError"
              valueKey='suffix'
              label='Suffix:'/>
          </div>
        </div>
        <div grid>
          <div column>
            <app-select
              v-model="v$.gender.$model"
              :required="true"
              :errorMessage="genderError"
              :invalid="v$.gender.$invalid"
              valueKey="gender"
              :options="genderOptions"
              label="Gender:"
            />
          </div>
          <div column>
            <app-date-input
              v-model="v$.dob.$model"
              :required="true"
              :errorMessage="dobError"
              :invalid="v$.dob.$invalid"
              valueKey="dob"
              label="Date of Birth:"
            />
          </div>
          <div column>
            <app-input
              v-model='v$.ssn.$model'
              :invalid="v$.ssn.$invalid"
              :errorMessage="ssnError"
              valueKey='ssn'
              type="password"
              label="SSN:"
            />
          </div>
        </div>
      </div>
      <div class="_text-center">
        <app-async-button
          type="submit"
          class="_mts _mbs --sm"
          :executing="submitting"
          :disabled="!formReady"
        >
          <template #buttonContent>
            <span>Submit and Continue</span>
          </template>
        </app-async-button>
      </div>
    </form>
  </div>
</template>

<script>
  import AppInput from '@atoms/Input';
  import AppSelect from '@atoms/Select';
  import AppDateInput from '@atoms/DateInput';
  import AppAsyncButton from '@molecules/AsyncButton.vue';
  import AsyncLoader from '@molecules/AsyncLoader';
  import AppStepPrimarySubscriber from './Steps/StepPrimarySubscriber';

  import SelectOptions from '@mixins/SelectOptions';
  import validationMessages from '@mixins/validationMessages';
  import RequestHelper from '@mixins/RequestHelper';
  // import ClassPlanFromSubGroupCkData from '@mixins/ClassPlanFromSubGroupCkData';
  import auth from '@mixins/auth';
  import formattable from '@mixins/formattable';
  import CoverageDatesHelper from '@mixins/CoverageDatesHelper';
  import ListErrorWarnings from '@mixins/ListErrorWarnings';

  import SearchApi from '@api/SearchApi';
  import { required, requiredIf, maxLength, alphaNum } from 'vuelidate/lib/validators';
  import { fourOrNineDigitsLong, onOrAfter, after, onOrBefore, nameAlphaNumeric, fileBoundIdValidation } from '@/utils';
  import { useGroupSelector } from '@composables/useGroupSelector';
  import { useStore, useRoute, useRouter } from '@vue3shims';
  import { useAddFamily } from '@composables/useAddFamily';
  import { useClassPlans } from '@composables/useClassPlans';

  import clientHealthId from '@mixins/clientHealthId';
  import ClientHealthId from '@views/MemberManagement/MemberEdit/ClientHealthId';
  import { computed, onBeforeMount, ref, watch } from '@vue/composition-api';
  import { useVuelidate } from '@vuelidate/core';
  import { useHiddenRouteParams } from '@composables/useHiddenRouteParams';

  export default {
    mixins: [
      ListErrorWarnings,
      // ClassPlanFromSubGroupCkData,
      RequestHelper,
      validationMessages,
      SelectOptions,
      auth,
      formattable,
      // CoverageDatesHelper,
      clientHealthId
    ],
    components: {
      AppAsyncButton,
      AsyncLoader,
      AppInput,
      AppSelect,
      AppDateInput,
      AppStepPrimarySubscriber,
      ClientHealthId
    },
    setup() {
      const store = useStore();
      const route = useRoute();
      const router = useRouter();
      const { hiddenRouteParams } = useHiddenRouteParams();

      const { 
        findFirstGroupByCk, 
        findFirstSubGroupByCk,
        groups, 
        selected,  
        subGroup,
        subGroups, 
        openGroupSelector, 
      } = useGroupSelector();

      const {
        classId,
        clientId,
        dob,
        effectiveDate,
        fileBoundId,
        firstName,
        gender,
        healthIdMatch,
        lastName,
        middleInitial,
        planId,
        ssn,
        suffix,
        termDate,
      } = useAddFamily();

      const {
        computedCurrentSubGroupPlanInfo,
        computedFilteredClasses,
        computedFilteredClassOptions,
        computedFilteredPlanOptions,
        computedMemberIdSource,
        getFirstClassByClassId,
        getFirstPlanByClassAndPlanId,
        requestClassPlansForSubGroup,
        requestingNewClassPlans,
      } = useClassPlans({member: {}, coverageHistory: {}});

      const classPlans = ref([]);
      const groupCk = computed({
        get() {
          return selected.value.groupCk;
        },
        set(groupCk) {
          // our select box only contains groupCk, we also need groupId to set in portal context
          const group = findFirstGroupByCk(groupCk);
          if (!group) {
            return;
          }
          if(selected.value.groupCk !== groupCk) {
            group.type = 'group';
            store.dispatch('groupSelector/SET_SELECTED', group);
            // store.dispatch('groupSelector/CLEAR_SELECTED', 'subGroup');
          }
        }
      });
      const isInternalUser = computed(() => store.getters['isInternalUser']);
      const submitting = ref(false);
      const listErrors = {
          firstName: {
            prefix: 'First name: ',
            errors: {
              nameAlphaNumeric: {}
            }
          },
          lastName: {
            prefix: 'Last name: ',
            errors: {
              nameAlphaNumeric: {}
            }
          },
          effectiveDate: {
            prefix: 'Effective date',
            errors: {
              onOrAfterDOB: {},
              onOrAfterSubGroupEffectiveDate: {},
              onOrAfterGroupEffectiveDate: {},
              onOrAfterClassEffectiveDate: {},
              onOrAfterPlanEffectiveDate: {},
              onOrBeforeSubGroupTermDate: {},
              onOrBeforeGroupTermDate: {},
              onOrBeforeClassTermDate: {},
              onOrBeforePlanTermDate: {}
            }
          },
          termDate: {
            prefix: 'Term date',
            errors: {
              afterEffectiveDate: {},
              onOrBeforeSubGroupTermDate: {},
              onOrBeforeGroupTermDate: {},
              onOrBeforeClassTermDate: {},
              onOrBeforePlanTermDate: {}
            }
          }
      }

      const groupDropdownOptions = computed(() => {
        // internal users return only the group selected in the portal context
        // console.log(`gddo`, isInternalUser.value, selected.value);
        if (isInternalUser.value) {
          return [
            {
              value: selected.value.groupCk,
              text: `${selected.value.groupName} - ${ selected.value.groupId }`,
            }
          ]
        }

        // external users return sorted groups they have memberMaintenanceAllowed
        // console.log(`groupDropdownOptions subGroups`, subGroups.value);
        let opts = subGroups.value
          .filter((sg) => sg.access.memberMaintenanceAllowed === true && sg.parentGroupCk === selected.value.parentGroupCk)
          .map((sg) => {
            return { 
              value: sg.groupCk, 
              text: `${sg.groupName} - ${sg.groupId}`,
            }
          })
          .sort((sg1, sg2) => sg1.text.localeCompare(sg2.text));
        
        // remove duplicates
        return Array.from(new Set(opts.map(JSON.stringify))).map(JSON.parse);
      });

      const handleGroupClicked = ($element) => {
        // open portal context for internal users since it will be easier for them to select group/subgroups
        // from there, while external users must only select groups/subgroups from the dropdown that they
        // have the memberMaintenance permission on

        if (selected.value.groupCk === '') {
          openPortalContext('group', $element);
        }
      }
      const handleSubGroupClicked = ($element) => {
        // console.log(`$element`, $element);
        if (selected.value.subGroupCk === '') {
          openPortalContext('subGroup', $element);
        }
      }
      const openPortalContext = (tab, element) => {
        // force a blur or the select dropdown will appear over portal context
        element.target.blur();
        setTimeout(() => {
          openGroupSelector(tab);
        }, 50);
      }
      // uncomment if rthey decide they want parent group on this page
      const parentGroupCk = computed(() => store.getters['groupSelector/selected'].parentGroupCk);

      const subGroupCk = computed({
        get() {
          if (isInternalUser.value) {
            return selected.value.subGroupCk;
          }

          // only return the subGroupCk in this context if they have memberMaintenanceAllowed for that group
          // console.log(`set`, subGroup);
          if(
               subGroup.value 
            && subGroup.value.access 
            && subGroup.value.access.memberMaintenanceAllowed === true
            ) {
            return selected.value.subGroupCk;
          }
          return '';
        },
        set(subGroupCk) {
          // console.log(`subgroup set`, subGroupCk);
          if(selected.value.subGroupCk !== subGroupCk) {
            store.dispatch('groupSelector/SET_SELECTED', { type: 'subGroup', subGroupCk });
          }
        }
      });

      const subGroupDropdownOptions = computed(() => {
        // internal users return only the subgroup selected
        if(isInternalUser.value === true) {
          return subGroups.value.map((sg) => {
            return {
              value: sg.subGroupCk,
              text: `${sg.subGroupName} - ${sg.subGroupId}`,
            }
          });
        }

        // external users return sorted subgroups they have memberMaintenanceAllowed
        let opts = subGroups.value
          .filter((sg) => {
            // console.log(`filter ${sg.groupCk} === ${groupCk.value} && ${sg.access.memberMaintenanceAllowed} === true`);
            return sg.groupCk === groupCk.value 
                && sg.access.memberMaintenanceAllowed === true;
            }
          )
          .map((sg) => {
            // console.log(`map: ${sg.subGroupName}`);
            return {
              value: sg.subGroupCk,
              text: `${sg.subGroupName} - ${sg.subGroupId}`,
            }
          })
          .sort((sg1, sg2) => sg1.text.localeCompare(sg2.text));

        // remove duplicates
        return Array.from(new Set(opts.map(JSON.stringify))).map(JSON.parse);
      });

      const subscriber = computed({
          get() {
            return store.getters['addFamily/subscriber'];
          },
          set(newVal) {
            // console.log('EDIT: user setter', newVal);
            store.commit('addFamily/SET_SUBSCRIBER', newVal);
          }
      });

      const validations = computed(() => {
        return {
          classId: {
            required,
          },
          clientId: {
            required: requiredIf(function() {
              return computedMemberIdSource.value === 'CS';
            }),
            maxLength: maxLength(15),
            alphaNum,
          },
          dob: {
            required,
          },
          effectiveDate: {
            required,
            onOrAfterDOB: onOrAfter('dob'),
            onOrAfterGroupEffectiveDate: onOrAfter('groupEffectiveDate'),
            onOrBeforeGroupTermDate: onOrBefore('groupTermDate'),
            onOrBeforeSubGroupTermDate: onOrBefore('subGroupTermDate'),
            onOrBeforeClassTermDate: onOrBefore('classTermDate'),
            onOrBeforePlanTermDate: onOrBefore('planTermDate'),
            onOrAfterSubGroupEffectiveDate: onOrAfter('subGroupEffectiveDate'),
            onOrAfterClassEffectiveDate: onOrAfter('classEffectiveDate'),
            onOrAfterPlanEffectiveDate: onOrAfter('planEffectiveDate')
          },
          fileBoundId: {
            required: requiredIf(function() {
              return this.isInternalUser;
            }),
            maxLength: maxLength(15),
            fileBoundIdValidation,
          },
          firstName: {
            required,
            maxLength: maxLength(15),
            nameAlphaNumeric,
          },
          gender: {
            required,
          },
          groupCk: {
            required,
          },
          lastName: {
            required,
            maxLength: maxLength(35),
            nameAlphaNumeric,
          },
          middleInitial: {
            maxLength: maxLength(1),
          },
          parentGroupCk: {},
          planId: {
            required,
          },
          ssn: {
            fourOrNineDigitsLong,
          },
          subGroupCk: {
            required,
          },
          suffix: {
            maxLength: maxLength(10),
          },
          termDate: {
            afterEffectiveDate: after('effectiveDate'),
            onOrBeforeSubGroupTermDate: onOrBefore('subGroupTermDate'),
            onOrBeforeGroupTermDate: onOrBefore('groupTermDate'),
            onOrBeforeClassTermDate: onOrBefore('classTermDate'),
            onOrBeforePlanTermDate: onOrBefore('planTermDate')
          },
    }
    });

      const v$ = useVuelidate(validations, {
        classId,
        clientId,
        dob,
        effectiveDate,
        fileBoundId,
        firstName,
        gender,
        groupCk,
        lastName,
        middleInitial,
        planId,
        ssn,
        subGroupCk,
        suffix,
        termDate,
      });

      onBeforeMount(() => {
        console.log(`OBM`, computedMemberIdSource.value);
        if (route.query.clear === true) {
          store.commit('addFamily/CLEAR_ALL');
        }

        if(selected.value.subGroupCk !== '') {
          requestClassPlansForSubGroup(selected.value.subGroupCk);
        }
        if(hiddenRouteParams.value.memberCk) {
          loadCoverages(hiddenRouteParams.value.memberCk);
        }
      });

      watch(subGroupCk, (nVal) => {
        // console.log(`sg watch`, nVal);
        if(nVal) {
          requestClassPlansForSubGroup(selected.value.subGroupCk);
        }
      });

      return {
        hiddenRouteParams,
        classId, 
        classPlans,
        clientId,
        computedCurrentSubGroupPlanInfo,
        computedFilteredClasses,
        computedFilteredClassOptions,
        computedFilteredPlanOptions,
        dob,
        effectiveDate,
        fileBoundId,
        firstName,
        gender,
        getFirstClassByClassId,
        getFirstPlanByClassAndPlanId,
        groupCk,
        groupDropdownOptions,
        groups,
        handleGroupClicked,
        handleSubGroupClicked,
        healthIdMatch,
        lastName,
        listErrors,
        middleInitial,
        // uncomment when they decide they want pagr here
        // parentGroupCk,
        // parentGroupDropdownOptions,
        planId,
        requestingNewClassPlans,
        selected,
        ssn,
        subGroupCk,
        subGroupDropdownOptions,
        subGroups,
        submitting,
        suffix,
        termDate,
        v$,
      }
    },
    computed: {
      // this is here because our ancient version of Vuelidate doesn't play
      //  nice with setup();  For the record, we are on 0.7.  The current version 
      // which DOES work with the version of Vue & composition api is: 2.0
      // The moral of the story?  Update your libs, kids!  It only gets worse.
      parentGroupCk() {
        return this.$store.getters['groupSelector/selected'].parentGroupCk;
      },
    },
    methods: {
      planFilter(plan) {
        return this.classId === plan.classId
      },
      search() {
        this.submitting = true
        new SearchApi({
          simulateExample:
            'Member Search-Add Member - 3to4 Match'
        })
          .searchMembers({
            groupCk: this.groupCk,
            subGroupCk: this.subGroupCk,
            memberIdSource: this.computedMemberIdSource,
            memberId: this.clientId,
            firstName: this.firstName,
            lastName: this.lastName,
            dateOfBirth: this.dob,
            ssn: this.ssn,
            dateOfService: this.now(),
            memberStatus: 'A',
            matchSearch: 'true'
          })
          .then(res => {
            // console.log(`selected:`, this.selected);
            this.submitting = false;

            this.$store.commit('addFamily/SET_GROUP_CK', this.groupCk)
            this.$store.commit('addFamily/SET_SUB_GROUP_CK', this.subGroupCk)
            this.$store.commit(
              'addFamily/SET_ADD_FAMILY_CONTEXT',
              this.selected
            )

            if (res.healthIdMatch !== null && res.healthIdMatch !== undefined) {
              this.$store.commit('addFamily/SET_HEALTH_ID_MATCH', res.healthIdMatch)
            }

            if (res.ssnMatch !== null && res.ssnMatch !== undefined) {
              this.$store.commit('addFamily/SET_SSN_MATCH', res.ssnMatch)
            }

            if (res.matchingMembers && res.matchingMembers.length > 0) {
              this.$store.commit(
                'addFamily/SET_SIMILAR_MEMBERS',
                res.matchingMembers
              )
              // console.log(`route to member-found`);
              this.$router.push({ name: 'member-found', query: { subgroupSecurityViolation: res.subgroupSecurityViolation } })
            } else {
              // console.log(`route to details`);
              this.$router.push({ name: 'primary-subscriber-details' })
            }
          })
          .catch(ex => {
            console.error(`error`, ex);
            this.submitting = false;
          // TODO: handle errors
          });
      }
    },
    computed: {
      formReady () {
        // console.log(`formReady`, this.v$);
        return !this.v$.$invalid
      },
      computedPlanOptions() {
        return this.plansInClassOptions(this.classId)
      },
    },
    // onBeforeCreate() {
    //   if (this.$route.query.clear === true) {
    //     this.$store.commit('addFamily/CLEAR_ALL');
    //   }
    // }
  }
</script>

<style lang="scss">

</style>
